import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  credentialSchemasApi,
  type AttributeDef,
  type AttributeType,
  type CredentialTypeSchemaResponse,
} from '@/api/credentialSchemas';
import { errorMessage } from '@/api/client';
import type { CredentialType } from '@/api/credentials';
import { confirmDialog } from '@/stores/dialog';

// Chei i18n — traducerea se face la render, nu la încărcarea modulului.
const TYPE_LABEL_KEYS: Record<CredentialType, string> = {
  password: 'admin_ui.settings.type_password',
  api_key: 'admin_ui.settings.type_api_key',
  ssh_key: 'admin_ui.settings.type_ssh_key',
  certificate: 'admin_ui.settings.type_certificate',
  totp_seed: 'admin_ui.settings.type_totp_seed',
  db_connection: 'admin_ui.settings.type_db_connection',
  secure_file: 'admin_ui.settings.type_secure_file',
  generic: 'admin_ui.settings.type_generic',
};

const ATTR_TYPES: AttributeType[] = [
  'text',
  'textarea',
  'number',
  'url',
  'email',
  'date',
  'secret',
  'file',
];

const KEY_REGEX = /^[a-z_][a-z0-9_]{0,31}$/;

// ==========================================================
// SchemaCard — un tip = un card colapsabil cu editor
// ==========================================================

interface SchemaCardProps {
  schema: CredentialTypeSchemaResponse;
  tenantId: string;
}

export function SchemaCard({ schema, tenantId }: SchemaCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<AttributeDef[]>(schema.attributes);
  const [error, setError] = useState<string | null>(null);

  // Sincronizare cu schema primită când se re-fetchează după save/reset.
  useEffect(() => {
    setDraft(schema.attributes);
  }, [schema.attributes]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(schema.attributes),
    [draft, schema.attributes],
  );

  const updateMutation = useMutation({
    mutationFn: () => credentialSchemasApi.update(tenantId, schema.type, draft),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['credential-schemas', tenantId] });
    },
    onError: (err) => {
      setError(errorMessage(err, t('admin_ui.settings.save_error')));
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => credentialSchemasApi.reset(tenantId, schema.type),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['credential-schemas', tenantId] });
    },
    onError: (err) => {
      if (err instanceof Error) setError(err.message);
      else setError(t('admin_ui.settings.reset_error'));
    },
  });

  const validate = (): string | null => {
    const seen = new Set<string>();
    for (const attr of draft) {
      if (!KEY_REGEX.test(attr.key)) {
        return t('admin_ui.settings.key_invalid', { key: attr.key });
      }
      if (seen.has(attr.key)) return t('admin_ui.settings.key_duplicate', { key: attr.key });
      seen.add(attr.key);
      if (!attr.label.trim()) return t('admin_ui.settings.label_empty', { key: attr.key });
    }
    return null;
  };

  const save = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    updateMutation.mutate();
  };

  const patchAttr = (idx: number, patch: Partial<AttributeDef>) => {
    setDraft((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const addAttr = () => {
    setDraft((prev) => [
      ...prev,
      {
        key: '',
        label: '',
        type: 'text',
        required: false,
        secret: false,
      },
    ]);
  };

  const removeAttr = (idx: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== idx));
  };

  const move = (idx: number, dir: -1 | 1) => {
    setDraft((prev) => {
      const next = prev.slice();
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const busy = updateMutation.isPending || resetMutation.isPending;

  return (
    <div className="border border-[var(--border-strong)] rounded-md">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--bg-hover)]/50 text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-[var(--text)]">
            {t(TYPE_LABEL_KEYS[schema.type])}
          </span>
          <span className="badge bg-[var(--bg-surface)] text-[var(--text-muted)] text-[10px]">
            {schema.type}
          </span>
          {schema.is_custom && (
            <span
              className="badge text-[10px]"
              style={{
                background: 'var(--accent-bg)',
                color: 'var(--accent)',
                border: '1px solid var(--accent)',
              }}
            >
              {t('admin_ui.settings.custom_badge')}
            </span>
          )}
          <span className="text-xs text-[var(--text-dim)]">
            {t('admin_ui.settings.attributes_count', { count: schema.attributes.length })}
          </span>
        </div>
        <span className="text-[var(--text-dim)] text-xs">{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div className="border-t border-[var(--border-strong)] p-4 space-y-3">
          <div className="grid grid-cols-[2fr_2fr_1.5fr_56px_56px_80px] gap-2 text-[10px] uppercase text-[var(--text-muted)] font-semibold px-1">
            <div>{t('admin_ui.settings.col_key')}</div>
            <div>{t('admin_ui.settings.col_label')}</div>
            <div>{t('admin_ui.settings.col_type')}</div>
            <div className="text-center">{t('admin_ui.settings.col_required')}</div>
            <div className="text-center">{t('admin_ui.settings.col_secret')}</div>
            <div className="text-center">{t('admin_ui.settings.col_order')}</div>
          </div>

          {draft.map((attr, idx) => {
            const isFirst = idx === 0;
            const isLast = idx === draft.length - 1;
            return (
              <div
                key={idx}
                className="grid grid-cols-[2fr_2fr_1.5fr_56px_56px_80px] gap-2 items-center"
              >
                <input
                  className="input font-mono text-xs py-1.5"
                  value={attr.key}
                  onChange={(e) => patchAttr(idx, { key: e.target.value })}
                  placeholder="snake_case"
                  disabled={busy}
                />
                <input
                  className="input text-xs py-1.5"
                  value={attr.label}
                  onChange={(e) => patchAttr(idx, { label: e.target.value })}
                  placeholder={t('admin_ui.settings.label_placeholder')}
                  disabled={busy}
                />
                <select
                  className="input text-xs py-1.5"
                  value={attr.type}
                  onChange={(e) => patchAttr(idx, { type: e.target.value as AttributeType })}
                  disabled={busy}
                >
                  {ATTR_TYPES.map((attrType) => (
                    <option key={attrType} value={attrType}>
                      {attrType}
                    </option>
                  ))}
                </select>
                <div className="flex justify-center">
                  <input
                    type="checkbox"
                    checked={attr.required}
                    onChange={(e) => patchAttr(idx, { required: e.target.checked })}
                    disabled={busy}
                  />
                </div>
                <div className="flex justify-center">
                  <input
                    type="checkbox"
                    checked={attr.secret}
                    onChange={(e) => patchAttr(idx, { secret: e.target.checked })}
                    disabled={busy}
                  />
                </div>
                <div className="flex gap-1 justify-center items-center">
                  {!isFirst ? (
                    <button
                      type="button"
                      className="text-[var(--text-dim)] hover:text-[var(--text)] text-sm px-1 leading-none"
                      onClick={() => move(idx, -1)}
                      disabled={busy}
                      title={t('admin_ui.settings.move_up')}
                    >
                      ↑
                    </button>
                  ) : (
                    <span className="w-5" aria-hidden="true" />
                  )}
                  {!isLast ? (
                    <button
                      type="button"
                      className="text-[var(--text-dim)] hover:text-[var(--text)] text-sm px-1 leading-none"
                      onClick={() => move(idx, 1)}
                      disabled={busy}
                      title={t('admin_ui.settings.move_down')}
                    >
                      ↓
                    </button>
                  ) : (
                    <span className="w-5" aria-hidden="true" />
                  )}
                  <button
                    type="button"
                    className="text-red-500 hover:text-red-700 text-xs px-1 leading-none"
                    onClick={() => removeAttr(idx)}
                    disabled={busy}
                    title={t('common.delete')}
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}

          <button
            type="button"
            className="btn-outline text-xs"
            onClick={addAttr}
            disabled={busy}
          >
            {t('admin_ui.settings.add_attribute')}
          </button>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-[var(--border)]">
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-primary text-xs"
                onClick={save}
                disabled={busy || !dirty}
              >
                {updateMutation.isPending ? t('admin_ui.settings.saving') : t('common.save')}
              </button>
              {dirty && (
                <button
                  type="button"
                  className="btn-outline text-xs"
                  onClick={() => {
                    setDraft(schema.attributes);
                    setError(null);
                  }}
                  disabled={busy}
                >
                  {t('security_section.discard')}
                </button>
              )}
            </div>
            {schema.is_custom && (
              <button
                type="button"
                className="text-xs text-red-600 hover:underline"
                onClick={async () => {
                  if (
                    await confirmDialog(
                      t('admin_ui.settings.reset_confirm', {
                        name: t(TYPE_LABEL_KEYS[schema.type]),
                      }),
                    )
                  ) {
                    resetMutation.mutate();
                  }
                }}
                disabled={busy}
              >
                {resetMutation.isPending ? t('admin_ui.settings.resetting') : t('admin_ui.settings.reset_default')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
